enum State {
  AL = 'Alabama',
  AK = 'Alaska',
  AZ = 'Arizona',
  AR = 'Arkansas',
  CA = 'California',
  CO = 'Colorado',
  CT = 'Connecticut',
  DE = 'Delaware',
  DC = 'District of Columbia',
  FL = 'Florida',
  GA = 'Georgia',
  HI = 'Hawaii',
  ID = 'Idaho',
  IL = 'Illinois',
  IN = 'Indiana',
  IA = 'Iowa',
  KS = 'Kansas',
  KY = 'Kentucky',
  LA = 'Louisiana',
  ME = 'Maine',
  MD = 'Maryland',
  MA = 'Massachusetts',
  MI = 'Michigan',
  MN = 'Minnesota',
  MS = 'Mississippi',
  MO = 'Missouri',
  MT = 'Montana',
  NE = 'Nebraska',
  NV = 'Nevada',
  NH = 'New Hampshire',
  NJ = 'New Jersey',
  NM = 'New Mexico',
  NY = 'New York',
  NC = 'North Carolina',
  ND = 'North Dakota',
  OH = 'Ohio',
  OK = 'Oklahoma',
  OR = 'Oregon',
  PA = 'Pennsylvania',
  PR = 'Puerto Rico',
  RI = 'Rhode Island',
  SC = 'South Carolina',
  SD = 'South Dakota',
  TN = 'Tennessee',
  TX = 'Texas',
  UT = 'Utah',
  VT = 'Vermont',
  VA = 'Virginia',
  WA = 'Washington',
  WV = 'West Virginia',
  WI = 'Wisconsin',
  WY = 'Wyoming',
}

type ZipInfo = {
  city: string;
  region: string;
  state_code: string;
  shipping_provider: string;
}

interface IBasicAddress {
  address: string;
  address2: string;
  city: string;
  state: keyof typeof State;
  zip: string;
}

interface IAddress extends IBasicAddress {
  delivery_instructions?: string;
  first_name: string;
  id?: number;
  last_name: string;
  local_phone_number?: string;
  primary: boolean;
  zip_code?: ZipInfo;
  sms_notification_status?:
    | 'sms_opted_out'
    | 'sms_opted_in'
    | 'sms_unsubscribed';
  sms_offers_status?:
    | 'sms_opted_out'
    | 'sms_opted_in'
    | 'sms_unsubscribed';
}

type AddressErrors =
  & { [key in keyof IAddress]?: string[] }
  & { base: string[] }

const zipCodeRegExp = /^\d{5}(-\d{4})?$/;
const telNumberRegExp = /^\d{10}$/;

class Address implements IAddress {
  address: string;
  address2: string;
  city: string;
  delivery_instructions: string;
  first_name: string;
  id: number;
  last_name: string;
  local_phone_number: string;
  primary: boolean;
  state: keyof typeof State;
  zip: string;
  zip_code: ZipInfo;
  sms_notification_status?:
    | 'sms_opted_out'
    | 'sms_opted_in'
    | 'sms_unsubscribed';
  sms_offers_status?:
    | 'sms_opted_out'
    | 'sms_opted_in'
    | 'sms_unsubscribed';

  constructor(attributes: IAddress) {
    Object.assign(this, attributes);
  }

  equals(address: IBasicAddress): boolean {
    return address
        && this.address === address.address
        && this.address2 === address.address2
        && this.city === address.city
        && this.state === address.state
        && this.zip === address.zip;
  }

  get fullName(): string {
    return `${this.first_name} ${this.last_name}`;
  }

  get firstLine(): string {
    return this.address;
  }

  get secondLine(): string {
    return this.address2;
  }

  get lastLine(): string {
    return `${this.city}, ${this.state} ${this.zip}`;
  }

  get hasValidTelNumber(): boolean {
    return telNumberRegExp.test(this.local_phone_number);
  }

  get hasValidZipCode(): boolean {
    return zipCodeRegExp.test(this.zip);
  }

  get isValid(): boolean {
    return this.hasValidTelNumber && this.hasValidZipCode;
  }
}

export { State };
export type { IAddress, ZipInfo, AddressErrors, IBasicAddress };
export default Address;
